// p  arcel.js is used to bundle the audio files into the bundle
import tapUrl from "url:../sounds/piece-tap.mp3";
import dingUrl from "url:../sounds/ding.mp3";
import cowbellUrl from "url:../sounds/cowbell.mp3";

// Add `suspended` and `interrupted` to the AudioContext state enum
declare global {
  interface AudioContext {
    // Mobile Safari sets the state to "interrupted" when the user
    // switches tabs or the phone locks.
    state: "suspended" | "interrupted" | "running" | "closed";
  }
}

// Call this when the user performs an action
export async function soundEffect(effect: keyof typeof audio) {
  const audioBuffer = audio[effect];

  if (!audioContext || !audioBuffer || !enableSound?.checked) return;

  // Hopefully this is always handled by our event listeners
  // but just in case, we'll resume the audio context here.
  // This won't work on iOS unless it's in response to a user interaction.
  await resumeAudioContext()
  const bufferSource = audioContext.createBufferSource();
  bufferSource.buffer = audioBuffer;
  bufferSource.connect(audioContext.destination);
  bufferSource.start(0);
  bufferSource.onended = () => bufferSource.disconnect();
}

// Create an audio context
const audioContext = window.AudioContext ? new window.AudioContext() : null;
const enableSound = document.getElementById("enable-sound") as HTMLInputElement | null;
if (enableSound) {
  const updateLabel = () => enableSound.labels![0].innerHTML = enableSound.checked ? "🔈" : "🔇";
  // Toggle the sound effects
  enableSound.addEventListener("change", () => {
    localStorage.setItem("enable-sound", enableSound.checked.toString());
    updateLabel();
  });
  enableSound.checked = localStorage.getItem("enable-sound") !== "false";
  updateLabel();
}

// Create an object to store the audio buffers
const audio = {} as { [key: string]: AudioBuffer | null; }

// Load the audio buffers
(async () => { audio.move = await loadAudioBuffer(tapUrl) })();
(async () => { audio.success = await loadAudioBuffer(dingUrl) })();
(async () => { audio.missed = await loadAudioBuffer(cowbellUrl) })();

// Load the sound effects into their respective buffers
async function loadAudioBuffer(url: string) {
  if (!audioContext) return null;

  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return audioContext.decodeAudioData(arrayBuffer);
}

function resumeAudioContext() {
  if (audioContext?.state === "suspended" || audioContext?.state === "interrupted") {
    return audioContext.resume();
  }
}

// If the audio context is suspended, resume it on the first user interaction
document.addEventListener("click", resumeAudioContext);
document.addEventListener("touchstart", resumeAudioContext);
document.addEventListener("keydown", resumeAudioContext);
